import { useAutoAnimate } from '~/common/hooks/useAutoAnimate';
import { Button } from '@mantine/core';
import { useForm } from '@mantine/form';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import { type AIAssertionModel, type AIAssertionScreenshotType } from '~/server/logic/steps/operation/types';
import { AIAssertionConfig } from './AIAssertionConfig';

export default function AIAssertionOpConfig({
  operation,
  onClose,
  onSave,
  readonly,
  optimisticPrompt,
}: {
  operation: SelectedOperation;
  onClose: () => void;
  onSave: (values: {
    assertion: string;
    includeScreenshot: boolean;
    screenshotType: AIAssertionScreenshotType;
    model: AIAssertionModel;
  }) => void;
  readonly?: boolean;
  optimisticPrompt?: string;
}) {
  const [animationParent] = useAutoAnimate();

  const form = useForm<{
    assertion: string;
    includeScreenshot: boolean;
    screenshotType: AIAssertionScreenshotType;
    model: AIAssertionModel;
  }>({
    initialValues: {
      assertion: optimisticPrompt || operation.aiAssertionOp?.assertion || '',
      includeScreenshot: operation.aiAssertionOp?.includeScreenshot ?? true,
      screenshotType: operation.aiAssertionOp?.screenshotType ?? 'VIEWPORT',
      model: operation.aiAssertionOp?.model || 'STABLY_FLASH',
    },
  });

  return (
    <form
      onSubmit={form.onSubmit(onSave)}
      ref={animationParent}
    >
      <AIAssertionConfig
        readonly={readonly}
        className="mt-2"
        assertion={form.values.assertion}
        includeScreenshot={form.values.includeScreenshot}
        screenshotType={form.values.screenshotType}
        model={form.values.model}
        onAssertionChange={(value) => form.setFieldValue('assertion', value)}
        onIncludeScreenshotChange={(value) => form.setFieldValue('includeScreenshot', value)}
        onScreenshotTypeChange={(value) => form.setFieldValue('screenshotType', value)}
        onModelChange={(value) => form.setFieldValue('model', value)}
        promptTitle="Prompt"
      />

      {!readonly && (
        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="subtle"
            onClick={onClose}
            size="xs"
            className="text-gray-600 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="xs"
          >
            Save
          </Button>
        </div>
      )}
    </form>
  );
}
